import { UserPreferencesKeysEnum } from 'features/settings/settingsTypes'

/**
 * Constants for tenant URL routing
 * 
 * TENANT_URL_PARAM: The URL parameter name used in routes (e.g., /:tntId)
 * CRITICAL: Use tntId (integer) in URLs, tenantId (GUID) in HTTP headers
 */
export const TENANT_URL_PARAM = 'tntId'

/**
 * Storage keys for tenant identifiers
 * tntId: Integer ID for URLs
 * tenantId: GUID for API headers
 */
export const TNTID_STORAGE_KEY = UserPreferencesKeysEnum.outboundAiPreferredTntId
export const TENANTID_STORAGE_KEY = UserPreferencesKeysEnum.outboundAiPreferredTenant

/**
 * Known route paths that can appear at the root level in old URLs (without tenantId)
 * Used to distinguish between old URLs and new tenant-prefixed URLs
 */
const KNOWN_ROUTE_PREFIXES = [
  'claims',
  'claim',
  'batch',
  'account',
  'analytics',
  'data-handling',
  'files-center',
  'file-share',
  'login',
  'logout',
  'service-unavailable',
  'noaccess-found'
]

/**
 * Extracts tntId (integer) from the current URL pathname
 * Expected URL format: /:tntId/...
 * 
 * @returns The tntId from URL, or null if not found
 * 
 * @example
 * // URL: /123/claims/claim/456
 * getTntIdFromUrl() // returns '123'
 * 
 * // URL: /claims/claim/456 (old format)
 * getTntIdFromUrl() // returns null
 */
export const getTntIdFromUrl = (pathname?: string): string | null => {
  const urlPath = pathname || window.location.pathname
  const segments = urlPath.split('/').filter(Boolean)
  
  // If no segments, no tntId
  if (segments.length === 0) {
    return null
  }
  
  const firstSegment = segments[0]
  
  // If first segment is a known route prefix, this is an old URL without tntId
  if (KNOWN_ROUTE_PREFIXES.includes(firstSegment)) {
    return null
  }
  
  // Otherwise, first segment is the tntId
  return firstSegment
}

/**
 * Gets tntId (integer) from sessionStorage
 * 
 * @returns The tntId from storage, or null if not found
 */
export const getTntIdFromStorage = (): string | null => {
  const tntId = sessionStorage.getItem(TNTID_STORAGE_KEY)
  return tntId
}

/**
 * Gets tenantId (GUID) from sessionStorage
 * Used for HTTP headers
 * 
 * @returns The tenantId from storage, or null if not found
 */
export const getTenantIdFromStorage = (): string | null => {
  const tenantId = sessionStorage.getItem(TENANTID_STORAGE_KEY)
  return tenantId
}

/**
 * Gets tntId, trying URL first, then falling back to sessionStorage
 * 
 * @returns The tntId from URL or storage, or null if not found
 */
export const getAvailableTntId = (): string | null => {
  return getTntIdFromUrl() || getTntIdFromStorage()
}

/**
 * Builds a URL path with tntId prefix
 * 
 * @param tntId - The tntId (integer) to include in the URL
 * @param path - The path after the tntId (e.g., '/claims' or '/dashboard')
 * @returns The full path with tntId prefix
 * 
 * @example
 * buildUrlWithTntId(123, '/claims') // returns '/123/claims'
 * buildUrlWithTntId('456', '/claims/claim/789') // returns '/456/claims/claim/789'
 */
export const buildUrlWithTntId = (tntId: string | number, path: string): string => {
  // Remove leading slash from path if present to avoid double slashes
  const cleanPath = path.startsWith('/') ? path.slice(1) : path
  const result = `/${tntId}/${cleanPath}`
  return result
}

/**
 * Checks if the current URL contains a tntId parameter
 * 
 * @param pathname - Optional pathname to check, defaults to current location
 * @returns true if tntId is in URL, false otherwise
 * 
 * @example
 * // URL: /123/claims
 * hasTntIdInUrl() // returns true
 * 
 * // URL: /claims/claim/456 (old format)
 * hasTntIdInUrl() // returns false
 */
export const hasTntIdInUrl = (pathname?: string): boolean => {
  return getTntIdFromUrl(pathname) !== null
}

/**
 * Extracts the path after the tntId from a URL
 * 
 * @param fullPath - The full URL path including tntId (e.g., '/123/claims/claim/456')
 * @returns The path without the tntId prefix (e.g., '/claims/claim/456')
 * 
 * @example
 * getPathWithoutTntId('/123/claims/claim/456') // returns '/claims/claim/456'
 */
export const getPathWithoutTntId = (fullPath: string): string => {
  const segments = fullPath.split('/').filter(Boolean)
  
  if (segments.length > 0) {
    // Remove first segment (tntId) and reconstruct path
    const pathSegments = segments.slice(1)
    return pathSegments.length > 0 ? `/${pathSegments.join('/')}` : '/'
  }
  
  return fullPath
}

/**
 * Gets the current tenant parameter name for use in routes
 * Returns :tntId for URL routing
 * 
 * @returns The tenant parameter string with colon (e.g., ':tntId')
 */
export const getTntIdParamForRoute = (): string => {
  return `:${TENANT_URL_PARAM}`
}

/**
 * Gets tntId for navigation purposes
 * Tries URL first, then sessionStorage
 * Returns a non-null value by attempting both sources
 * 
 * @returns The tntId for use in navigation, or empty string if not found
 */
export const getTntIdForNavigation = (): string => {
  const tntId = getTntIdFromUrl() || getTntIdFromStorage() || ''
  return tntId
}

/**
 * Sets tntId in sessionStorage with logging
 * 
 * @param tntId - The tntId (integer) to store
 */
export const setTntIdInStorage = (tntId: string | number): void => {
  sessionStorage.setItem(TNTID_STORAGE_KEY, String(tntId))
}

/**
 * Sets tenantId (GUID) in sessionStorage with logging
 * 
 * @param tenantId - The tenantId (GUID) to store
 */
export const setTenantIdInStorage = (tenantId: string): void => {
  sessionStorage.setItem(TENANTID_STORAGE_KEY, tenantId)
}
